import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  CheckCircle,
  ShieldAlert,
  Clock,
  UserCheck,
  LogIn,
  Mail,
  KeyRound,
} from 'lucide-react';
import { invitationsApi, type InvitationPreview } from '../../api/client';
import { useAuthStore } from '../../store/useAuthStore';
import { Spinner } from '../../components/common/Spinner';

// ── Password strength ──────────────────────────────────────────────────────────

const MIN_PW = 8;

function pwStrength(pw: string): number {
  return (
    (pw.length >= MIN_PW ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0)
  );
}

// ── Types ──────────────────────────────────────────────────────────────────────

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'accepted' }
  | { kind: 'ready'; preview: InvitationPreview }
  | { kind: 'wrong_account'; maskedEmail: string }
  | { kind: 'create_account'; preview: InvitationPreview; error: string | null }
  | { kind: 'confirm_email' }
  | { kind: 'accepting' }
  | { kind: 'set_password'; orgName: string; error: string | null }
  | { kind: 'success'; orgName: string };

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer',
};

// ── Component ──────────────────────────────────────────────────────────────────

export function InvitationAcceptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { session, user, signUp, updatePassword, setOrgContext } = useAuthStore();

  const [state, setState] = useState<PageState>({ kind: 'loading' });

  // ── Preview load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }

    invitationsApi
      .preview(token)
      .then((preview) => {
        if (preview.status === 'accepted') return setState({ kind: 'accepted' });
        if (preview.status === 'revoked')  return setState({ kind: 'revoked' });
        if (preview.status === 'expired' || new Date(preview.expiresAt) < new Date()) {
          return setState({ kind: 'expired' });
        }

        // If the user is authenticated, validate email match
        if (session && user?.email) {
          const maskedLocal = preview.maskedEmail.split('@')[0];
          const first = maskedLocal[0];
          const last  = maskedLocal[maskedLocal.length - 1];
          const userLocal  = user.email.toLowerCase().split('@')[0];
          const userDomain = user.email.toLowerCase().split('@')[1];
          const invDomain  = preview.maskedEmail.split('@')[1];

          if (userDomain !== invDomain || userLocal[0] !== first || userLocal[userLocal.length - 1] !== last) {
            return setState({ kind: 'wrong_account', maskedEmail: preview.maskedEmail });
          }
        }

        setState({ kind: 'ready', preview });
      })
      .catch(() => setState({ kind: 'invalid' }));
  }, [token, session, user?.email]);

  // ── Accept invitation ────────────────────────────────────────────────────────

  async function handleAccept() {
    if (!token) return;
    setState({ kind: 'accepting' });
    try {
      const result = await invitationsApi.accept(token);
      setOrgContext({
        organizationId: result.organizationId,
        organizationName: result.organizationName,
        role: result.role,
      });

      // If the user authenticated via magic link they may have no password yet.
      // Detect this by checking if they only have one identity and it has no password provider.
      const identities = user?.identities ?? [];
      const hasPassword = identities.some(
        (id) => id.provider === 'email' && id.identity_data?.['email_verified'],
      );
      const isNewUser = !hasPassword && identities.length <= 1;

      if (isNewUser) {
        setState({ kind: 'set_password', orgName: result.organizationName ?? 'your organization', error: null });
      } else {
        setState({ kind: 'success', orgName: result.organizationName ?? 'your organization' });
        setTimeout(() => navigate('/baseball/mlb/command-center', { replace: true }), 1800);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('already a member'))           setState({ kind: 'accepted' });
      else if (message.includes('expired'))               setState({ kind: 'expired' });
      else if (message.includes('revoked'))               setState({ kind: 'revoked' });
      else if (message.includes('invitation was sent to')) {
        const match = message.match(/sent to (.+)\./);
        setState({ kind: 'wrong_account', maskedEmail: match?.[1] ?? '***' });
      } else {
        setState({ kind: 'invalid' });
      }
    }
  }

  // ── Create account (inline) ──────────────────────────────────────────────────

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const strength = useMemo(() => pwStrength(password), [password]);
  const strengthLabel = strength <= 1 ? 'Weak' : strength <= 3 ? 'Moderate' : 'Strong';

  async function handleCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    if (state.kind !== 'create_account') return;

    if (password !== confirm) {
      setState({ ...state, error: 'Passwords do not match.' });
      return;
    }
    if (password.length < MIN_PW) {
      setState({ ...state, error: 'Password must be at least 8 characters.' });
      return;
    }

    setState({ ...state, error: null });
    try {
      await signUp(email.trim(), password);
      navigate('/confirm-email', { state: { email: email.trim(), inviteToken: token } });
    } catch (err: unknown) {
      setState({
        ...state,
        error: err instanceof Error ? err.message : 'Sign-up failed. Please try again.',
      });
    }
  }

  // ── Set password ─────────────────────────────────────────────────────────────

  const [newPw, setNewPw]     = useState('');
  const [newConfirm, setNewConfirm] = useState('');
  const newStrength = useMemo(() => pwStrength(newPw), [newPw]);
  const newStrengthLabel = newStrength <= 1 ? 'Weak' : newStrength <= 3 ? 'Moderate' : 'Strong';

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (state.kind !== 'set_password') return;

    if (newPw !== newConfirm) {
      setState({ ...state, error: 'Passwords do not match.' });
      return;
    }
    if (newPw.length < MIN_PW) {
      setState({ ...state, error: 'Password must be at least 8 characters.' });
      return;
    }

    const orgName = state.orgName;
    setState({ ...state, error: null });
    try {
      await updatePassword(newPw);
      setState({ kind: 'success', orgName });
      setTimeout(() => navigate('/baseball/mlb/command-center', { replace: true }), 1800);
    } catch (err: unknown) {
      setState({
        kind: 'set_password',
        orgName,
        error: err instanceof Error ? err.message : 'Failed to set password. Please try again.',
      });
    }
  }

  // ── Derived URLs ─────────────────────────────────────────────────────────────

  const inviteUrl = `/invite?token=${token}`;
  const loginUrl  = `/login?next=${encodeURIComponent(inviteUrl)}`;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <span className="text-brand font-bold text-xl tracking-tight">
            Diamond<span className="text-gray-100">Edge</span>
          </span>
        </div>

        <div className="bg-surface-1 border border-surface-4 rounded-xl shadow-2xl overflow-hidden">

          {/* ── Loading ── */}
          {state.kind === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 p-12">
              <Spinner size="lg" />
              <p className="text-sm text-gray-500">Loading invitation…</p>
            </div>
          )}

          {/* ── Accepting ── */}
          {state.kind === 'accepting' && (
            <div className="flex flex-col items-center justify-center gap-3 p-12">
              <Spinner size="lg" />
              <p className="text-sm text-gray-500">Joining organization…</p>
            </div>
          )}

          {/* ── Set password (post-accept, for magic-link users) ── */}
          {state.kind === 'set_password' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
                <KeyRound className="w-6 h-6 text-brand" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-1">
                Set your password
              </h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                You're in! Create a password to secure your account.
              </p>

              {state.error && (
                <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
                  {state.error}
                </div>
              )}

              <form onSubmit={(e) => void handleSetPassword(e)} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="new-pw">
                    Password
                  </label>
                  <input
                    id="new-pw"
                    type="password"
                    required
                    autoFocus
                    autoComplete="new-password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    placeholder="At least 8 characters"
                  />
                  <div className="mt-2">
                    <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                      <div className="h-full bg-brand transition-all" style={{ width: `${(newStrength / 4) * 100}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Password strength: {newStrengthLabel}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="new-confirm">
                    Confirm password
                  </label>
                  <input
                    id="new-confirm"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={newConfirm}
                    onChange={(e) => setNewConfirm(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                    placeholder="Re-enter password"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <KeyRound className="w-4 h-4" />
                  Set password & continue
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/baseball/mlb/command-center', { replace: true })}
                  className="w-full text-xs text-gray-600 hover:text-gray-400 transition-colors text-center"
                >
                  Skip for now
                </button>
              </form>
            </div>
          )}

          {/* ── Success ── */}
          {state.kind === 'success' && (
            <div className="flex flex-col items-center justify-center gap-4 p-12">
              <CheckCircle className="w-14 h-14 text-win" />
              <h2 className="text-xl font-semibold text-gray-100 text-center">
                Welcome to {state.orgName}!
              </h2>
              <p className="text-sm text-gray-500 text-center">Taking you to your dashboard…</p>
            </div>
          )}

          {/* ── Invalid ── */}
          {state.kind === 'invalid' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-loss/10 border border-loss/25 mx-auto mb-4">
                <ShieldAlert className="w-6 h-6 text-loss" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-2">Invalid invitation</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                This invitation link is invalid or has already been used. Ask your team admin to send a new one.
              </p>
              <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                Sign in to Navyrx Sports
              </Link>
            </div>
          )}

          {/* ── Expired ── */}
          {state.kind === 'expired' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-warn/10 border border-warn/25 mx-auto mb-4">
                <Clock className="w-6 h-6 text-warn" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-2">Invitation expired</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                This invitation link is no longer valid. Ask your team admin to send a fresh invitation.
              </p>
              <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                Sign in to Navyrx Sports
              </Link>
            </div>
          )}

          {/* ── Revoked ── */}
          {state.kind === 'revoked' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-loss/10 border border-loss/25 mx-auto mb-4">
                <ShieldAlert className="w-6 h-6 text-loss" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-2">Invitation revoked</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                This invitation was cancelled by your team admin. Contact them if you believe this is a mistake.
              </p>
              <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                Sign in to Navyrx Sports
              </Link>
            </div>
          )}

          {/* ── Already accepted ── */}
          {state.kind === 'accepted' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-win/10 border border-win/25 mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-win" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-2">Already accepted</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                This invitation has already been used. Sign in to access your workspace.
              </p>
              <Link to="/login" className="btn-primary w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                Sign in
              </Link>
            </div>
          )}

          {/* ── Wrong account ── */}
          {state.kind === 'wrong_account' && (
            <div className="p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-warn/10 border border-warn/25 mx-auto mb-4">
                <ShieldAlert className="w-6 h-6 text-warn" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 text-center mb-2">Wrong account</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                This invitation was sent to{' '}
                <span className="text-gray-300 font-medium">{state.maskedEmail}</span>.
                Please sign in with that account to accept it.
              </p>
              <Link to={loginUrl} className="btn-primary w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                Sign in with a different account
              </Link>
            </div>
          )}

          {/* ── Confirm email (after inline sign-up) ── */}
          {state.kind === 'confirm_email' && (
            <div className="p-8 text-center">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
                <Mail className="w-6 h-6 text-brand" />
              </div>
              <h2 className="text-xl font-semibold text-gray-100 mb-2">Check your email</h2>
              <p className="text-sm text-gray-500">
                We sent a confirmation link to your email. Click it to verify your account — you'll be
                brought back here to complete joining the organization.
              </p>
            </div>
          )}

          {/* ── Create account (inline) ── */}
          {state.kind === 'create_account' && (
            <div>
              <div className="bg-brand/5 border-b border-surface-4 px-8 py-5 text-center">
                <h2 className="text-base font-semibold text-gray-100 mb-0.5">Create your account</h2>
                <p className="text-xs text-gray-500">
                  to join{' '}
                  <span className="text-brand font-medium">{state.preview.organizationName ?? 'the organization'}</span>
                </p>
              </div>

              <div className="px-8 py-6">
                {state.error && (
                  <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
                    {state.error}
                  </div>
                )}

                <form onSubmit={(e) => void handleCreateAccount(e)} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="ca-email">
                      Email
                    </label>
                    <input
                      id="ca-email"
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                      placeholder="your@email.com"
                    />
                    <p className="mt-1 text-[11px] text-gray-600">
                      Must match the address this invitation was sent to.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="ca-password">
                      Password
                    </label>
                    <input
                      id="ca-password"
                      type="password"
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                      placeholder="At least 8 characters"
                    />
                    <div className="mt-2">
                      <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
                        <div className="h-full bg-brand transition-all" style={{ width: `${(strength / 4) * 100}%` }} />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Password strength: {strengthLabel}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="ca-confirm">
                      Confirm password
                    </label>
                    <input
                      id="ca-confirm"
                      type="password"
                      required
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
                      placeholder="Re-enter password"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    <KeyRound className="w-4 h-4" />
                    Create account & join
                  </button>
                </form>

                <button
                  onClick={() => setState({ kind: 'ready', preview: state.preview })}
                  className="mt-4 w-full text-xs text-gray-600 hover:text-gray-400 transition-colors text-center"
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* ── Ready (invite details) ── */}
          {state.kind === 'ready' && (
            <div>
              <div className="bg-brand/5 border-b border-surface-4 px-8 py-6 text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-brand" />
                </div>
                <h2 className="text-lg font-semibold text-gray-100 mb-1">You've been invited</h2>
                <p className="text-sm text-gray-500">
                  to join{' '}
                  <span className="text-brand font-semibold">{state.preview.organizationName ?? 'an organization'}</span>
                  {' '}on Navyrx Sports
                </p>
              </div>

              <div className="px-8 py-6">
                <div className="bg-surface-2 border border-surface-4 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Organization</p>
                      <p className="text-sm font-semibold text-gray-100">{state.preview.organizationName ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Your Role</p>
                      <p className="text-sm font-semibold text-brand">
                        {ROLE_LABELS[state.preview.role] ?? state.preview.role}
                      </p>
                    </div>
                    {state.preview.inviterName && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Invited by</p>
                        <p className="text-sm text-gray-300">{state.preview.inviterName}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Expires</p>
                      <p className="text-sm text-gray-300">
                        {new Date(state.preview.expiresAt).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-surface-4">
                    <p className="text-xs text-gray-500">
                      Sent to{' '}
                      <span className="text-gray-400 font-medium">{state.preview.maskedEmail}</span>
                    </p>
                  </div>
                </div>

                {session ? (
                  /* Authenticated: accept directly */
                  <>
                    <button
                      onClick={() => void handleAccept()}
                      className="btn-primary w-full flex items-center justify-center gap-2 mb-3"
                    >
                      <UserCheck className="w-4 h-4" />
                      Accept Invitation
                    </button>
                    <p className="text-xs text-gray-600 text-center">
                      Accepting as <span className="text-gray-500">{user?.email}</span>
                    </p>
                  </>
                ) : (
                  /* Unauthenticated: sign in or create account inline */
                  <div className="space-y-3">
                    <Link
                      to={loginUrl}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                    >
                      <LogIn className="w-4 h-4" />
                      Sign in to accept
                    </Link>
                    <button
                      onClick={() => setState({ kind: 'create_account', preview: state.preview, error: null })}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-surface-4 text-sm text-gray-300 bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                      <KeyRound className="w-4 h-4" />
                      New here? Create account to join
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
